import { useCameraName } from '../../../shared/hooks/useCameraName'
import { useClipLoaderState } from '../../../shared/hooks/useClipLoaderState'
import { useClipLoaderTracking } from '../../../shared/hooks/useClipLoaderTracking'
import { useLastClipPreview } from '../../../shared/hooks/useLastClipPreview'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'

import { FacilityType } from '@/graphql/generated/schemas'
import { isIngestedEmailIncident, isManualIncident } from '@/utils/incidents'

import {
  IncidentDataType,
  MixpanelDataIProps,
} from '../../../shared/types/types'
import { ClipLoader } from './ClipLoader'
import { IncidentVideo } from './IncidentVideo'
import { MarketIncidentMap } from './MarketIncidentMap'
import { IncidentImages } from './incident-images/IncidentImages'

export interface MediaPlayerIProps {
  incident: IncidentDataType
  isLoading: boolean
  mixpanelData: MixpanelDataIProps
  scrollToTop: () => void
  /** Render the view-toolbar player (used by both the legacy and redesigned drawers). */
  useViewToolbar?: boolean
}

export const MediaPlayer = ({
  incident,
  isLoading,
  mixpanelData,
  scrollToTop,
  useViewToolbar = false,
}: MediaPlayerIProps) => {
  const {
    shouldShowClipLoader,
    shouldShowIncidentVideo,
    clipLabel,
    shouldDefaultToLive,
    hasTimedOut,
    hasClickedLive,
    handleClipClick,
    handleLiveClick,
  } = useClipLoaderState({
    incidentId: incident?.id,
    incidentType: incident?.type,
    hasVideoClip: incident?.hasVideoClip,
    createdAt: incident?.createdAt,
    isLoading,
  })

  const { onClipLoaderShown, trackViewedLive, trackClipClicked } =
    useClipLoaderTracking({
      incidentId: incident?.id,
      mixpanelData,
      hasTimedOut,
      hasVideoClip: incident?.hasVideoClip,
      hasClickedLive,
    })

  const device = incident?.devices?.[0]

  const isClipLoaderPreviewEnabled =
    useFeatureFlag(FeatureFlag.clipLoaderPreview) ||
    useFeatureFlag(FeatureFlag.development)

  // Manual and Email Incidents have no camera, so no clip to wait for.
  const hasCameraMedia =
    incident != null &&
    !isManualIncident(incident) &&
    !isIngestedEmailIncident(incident)

  // ClipLoader only renders for camera incidents in the pre-clip window;
  // gate the preview and door -> camera lookups on the same condition (ENG-31021).
  const shouldRenderClipLoader = shouldShowClipLoader && hasCameraMedia

  const {
    previewImageUrl,
    HiddenPlayer,
    previewClipAgeSeconds,
    previewLoadDurationMs,
  } = useLastClipPreview({
    deviceId: device?.id,
    enabled: shouldRenderClipLoader && isClipLoaderPreviewEnabled,
  })

  const cameraName = useCameraName({
    deviceId: device?.id,
    deviceName: device?.name,
    deviceType: device?.type,
    enabled: shouldRenderClipLoader,
  })

  if (!incident) {
    return null
  }

  const handleLiveClickWithAnalytics = () => {
    trackViewedLive()
    handleLiveClick()
  }

  const handleClipClickWithAnalytics = () => {
    trackClipClicked()
    handleClipClick()
  }

  return (
    <>
      {shouldRenderClipLoader && (
        <>
          <HiddenPlayer />
          <ClipLoader
            deviceName={cameraName}
            hasTimedOut={hasTimedOut}
            hasVideoClip={incident?.hasVideoClip}
            incidentId={incident?.id}
            mixpanelData={mixpanelData}
            onClipClick={handleClipClickWithAnalytics}
            onLiveClick={handleLiveClickWithAnalytics}
            onShown={onClipLoaderShown}
            previewClipAgeSeconds={previewClipAgeSeconds}
            previewImageUrl={previewImageUrl}
            previewLoadDurationMs={previewLoadDurationMs}
            useViewToolbar={useViewToolbar}
          />
        </>
      )}
      {hasCameraMedia && shouldShowIncidentVideo && (
        <IncidentVideo
          clipLabel={clipLabel}
          createdAt={incident?.createdAt}
          defaultToLive={shouldDefaultToLive}
          deviceId={device?.id}
          hasTimedOut={hasTimedOut}
          hasVideoClip={incident?.hasVideoClip}
          incidentId={incident?.id}
          incidentType={incident?.type}
          mixpanelData={mixpanelData}
          onClipClick={handleClipClickWithAnalytics}
          scrollToTop={scrollToTop}
          useViewToolbar={useViewToolbar}
        />
      )}
      {incident?.facility?.type === FacilityType.Market &&
        !isIngestedEmailIncident(incident) && (
          <MarketIncidentMap incident={incident} />
        )}
      {incident?.imageUrls?.length > 0 && (
        <IncidentImages images={incident?.imageUrls} />
      )}
    </>
  )
}
